import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", company: "", email: "", phone: "", address: "", notes: "" };

export default function Clients() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/clients").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) await api.put(`/clients/${editId}`, form);
      else await api.post("/clients", form);
      toast.success(editId ? "Client updated" : "Client added");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted"); load();
  };

  const edit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle={`${list.length} companies · CRM`}
        actions={
          <button data-testid="new-client-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
            className="bg-white text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 hover:bg-white/90 transition-colors active:scale-[0.98]">
            <Plus size={14} weight="bold" /> New Client
          </button>
        }
      />
      <div className="p-8">
        <div className="hairline rounded-lg bg-[#0F0F0F] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-white/40">
              <tr className="hairline-b">
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-right px-4 py-3 font-medium">Projects</th>
                <th className="text-right px-4 py-3 font-medium">Revenue</th>
                <th className="text-right px-4 py-3 font-medium">Pending</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {list.map((c, i) => (
                <tr key={c.id} className={`hover:bg-white/[0.02] transition-colors ${i % 2 === 0 ? "" : "bg-white/[0.01]"}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-white/50">{c.company}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-white/60">
                    <div>{c.email}</div>
                    <div>{c.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-right mono">{c.projects_count}</td>
                  <td className="px-4 py-3 text-right mono text-emerald-400">{fmtINR(c.revenue)}</td>
                  <td className="px-4 py-3 text-right mono text-amber-400">{fmtINR(c.pending)}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-client-${c.id}`} onClick={() => edit(c)} className="text-white/40 hover:text-white p-1"><PencilSimple size={14} /></button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => del(c.id)} className="text-white/40 hover:text-red-400 p-1"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-white/40">No clients yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Name*"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["notes", "Notes"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-white/40">{l}</label>
                <input data-testid={`client-input-${k}`} value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})}
                  className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:border-white/30 focus:outline-none" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button data-testid="save-client-btn" onClick={save} className="bg-white text-black text-xs font-semibold px-4 py-2 rounded-md hover:bg-white/90">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
